//@flow
import * as Immutable from 'immutable';
import EntityImportDataHelper from 'common/entity/helpers/EntityImportDataHelper';
import I18nHelper from 'common/helpers/I18nHelper';
import { Contact } from 'common/models/internal/Contact';
import type { EntityImportRowModelType } from 'common/models/flow/EntityImportTable';
import { LatLng } from 'common/models/internal/LatLng';
import DataUtils from 'common/utils/DataUtils';
import { MasterLocation } from 'common/models/internal/MasterLocation';
import { EntityImportHelper } from 'common/entity/helpers/EntityImportHelper';
import type {
    TableColumnType,
    UpdateFilteredColumnsTypes
} from 'common/entity/generic/EntityImportContainer';
import { HomebaseId } from 'common/models/internal/HomebaseId';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export class LocationImportHelper extends EntityImportDataHelper {
    static getContactIdErrorMessage({ exisitingEntityIds, validationIds }) {
        if (!exisitingEntityIds) {
            return;
        }
        const exisitingEntityIdsSet = new Set();
        exisitingEntityIds.forEach(id => exisitingEntityIdsSet.add(id));
        const contactIdsFound = validationIds.filter(id =>
            exisitingEntityIdsSet.has(id)
        );
        if (contactIdsFound.length) {
            return nsTranslate('validations:entityIdExists', {
                id: contactIdsFound.join(', ')
            });
        }
    }

    /**
     * finding `dayOfWeek` `index` in `timeSlots` and `updating` `dayIndex` in accessor's first index
     * @param {Object} { accessor, entity }
     */
    static getTimeSlotsAccessor = accessor =>
        EntityImportDataHelper.getTimeSlotsAccessor({
            ...accessor,
            workingDaysAccessor: ['timeSlots'],
            dayOfWeekIndex: 1
        });

    static getContactAccessor = ({ accessor, entity }) => {
        if (entity.contact) {
            return accessor;
        }
        return ['contact'];
    };

    static serializeContact = (value, { entity, cellId }): Contact | string => {
        const contactInnerKey = cellId.header.getAccessor().last();
        if (!entity.contact) {
            // When contact is undefined create new contact object
            return Contact.fromJS({ [contactInnerKey]: value });
        }
        if (cellId.headerId === 'contactTeams') {
            // When contact team is getting updated create new team object
            return LocationImportHelper.serializeTeams(value);
        }
        return value;
    };

    static processEntityRows = (
        {
            rows
        }: {
            rows: Immutable.List<EntityImportRowModelType>
        },
        props
    ): Immutable.List<EntityImportRowModelType> => {
        const { clientId } = props;
        return rows.map(entityRow => {
            const updatedLocationRow = entityRow.map((cellValue, headerId) => {
                switch (headerId) {
                    case 'timeSlots':
                        return cellValue.toList();
                    case 'latLng':
                        return LatLng.sanitizeLatLng(cellValue.toJS());
                    case 'customProperties':
                        return cellValue.filter(value => value);
                    case 'contactId':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('contactId'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    case 'contact':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('id'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    case 'vehicleModelId':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('modelId'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    case 'crossDockIds':
                        return cellValue &&
                            !DataUtils.isEmpty(cellValue.get('homebaseId'))
                            ? cellValue.set('clientId', clientId)
                            : null;
                    default:
                        return cellValue;
                }
            });

            return MasterLocation.fromJS(updatedLocationRow.toJS());
        });
    };

    static getUploadedEntities = ({
        uploadedTable,
        clientId,
        getDataForCell
    }): Immutable.List<EntityImportRowModelType> => {
        const locationsData = EntityImportHelper.makeDataForEntities({
            table: uploadedTable,
            clientId,
            getDataForCell,
            processEntityRows: LocationImportHelper.processEntityRows
        });
        return Immutable.List(locationsData);
    };

    static generateEntityList = (
        locationsData: Array<*>
    ): Immutable.List<MasterLocation> =>
        Immutable.List(
            locationsData.map(locationData =>
                MasterLocation.fromJS(locationData)
            )
        );

    static updateFilteredColumnsAfterUpload = ({
        columns,
        file
    }: UpdateFilteredColumnsTypes): Array<TableColumnType> => {
        const newContactIdColumn = columns.find(
            ({ id }) => id === 'newContactId'
        );
        const isNewContactIdPresentInCSV = file.fileHeaders.find(
            header =>
                header.toLowerCase() === newContactIdColumn.title.toLowerCase()
        );
        if (!isNewContactIdPresentInCSV) {
            let contactPhoneNumberIndex = -1;
            let contactNameIndex = -1;
            columns.findIndex(({ id }, index) => {
                if (id === 'contactName') {
                    contactNameIndex = index;
                } else if (id === 'contactNumber') {
                    contactPhoneNumberIndex = index;
                } else if (
                    contactPhoneNumberIndex !== -1 &&
                    contactNameIndex !== -1
                ) {
                    return true;
                }
                return false;
            });
            const updatedColumns = [...columns];
            [contactPhoneNumberIndex, contactNameIndex].forEach(index => {
                updatedColumns[index] = {
                    ...updatedColumns[index],
                    disabled: true
                };
            });
            return updatedColumns;
        }
        return columns;
    };

    static getCustomFields(entitySettings) {
        return entitySettings && entitySettings.locationSettings.customFields;
    }

    static serializeHomebaseId(homebaseId: string): ?HomebaseId {
        return homebaseId ? HomebaseId.fromJS({ homebaseId }) : null;
    }

    static processHomebaseIds({ cellValue, clientId }) {
        return cellValue
            ? cellValue
                  .split(',')
                  .filter(homebaseId => homebaseId?.trim())
                  .map(homebaseId => {
                      return {
                          homebaseId: homebaseId.trim(),
                          clientId
                      };
                  })
            : [];
    }
}

export default LocationImportHelper;
